import type {
  CreatePersonCommandData,
  IdentificationCreationData,
} from '@festina/legalentity-general-types/legalentity'
import { typeUtils } from '@festina-life/core'
import {
  FormikCheckbox,
  FormikDatePicker,
  FormikFieldArray,
  FormikSelect,
  FormikTextInput,
} from '@festina-life/ui/formikForms'
import { useTranslate, useTranslateEnum } from '@festina-life/ui/hooks'
import * as Yup from 'yup'

import { useEnumOptions } from '../../hooks/useEnumOptions'
import { useHook, useService } from '../../lenteHooks'
import { newDefaultAddressLink } from '../../utility/newDefaultAddressLink'
import type { CommandDataFormProps } from '../commandActions/CommandDataFormAndSimulation'
import { LifeLazyForm } from '../forms/LifeLazyForm'
import { AddressLinkFields } from './AddressLinkFields'

type FormType = {
  identificationCreationData?: IdentificationCreationData
} & CreatePersonCommandData

const validateKey = typeUtils.createValidator<FormType>()

export const CreatePersonForm = ({
  commandData,
  shouldSkipFrame,
  commandActionVariant,
  ...props
}: CommandDataFormProps<FormType>) => {
  const translate = useTranslate()
  const translateEnum = useTranslateEnum()
  const informationSources = useHook('pages.crm.useCrmInformationSources')()
  const { minLength } = useService('logic.legalEntity.validateSsn')()
  const genderOptions = useEnumOptions('Gender', 'crm')

  const formSchema =
    commandActionVariant === 'create'
      ? Yup.object({
          identificationCreationData: Yup.object({
            externalId: Yup.string()
              .min(
                minLength,
                translate('validation.length', {
                  length: minLength,
                }),
              )
              .required(translate('validation.required')),
          }),
          birthDate: Yup.date().required(translate('validation.required')),
        })
      : undefined

  return (
    // Using LifeLazyForm with Formik input fields in CreatePersonForm to avoid an error in AddressLinkFields,
    // which uses Life input fields. AddressLinkFields use Life input fields because it is also
    // used in ChangeAddressForm and NlChangeAddressForm where LifeLazyForm is being used.
    <LifeLazyForm
      mapInitialValues={(): FormType => ({
        _type: 'CreatePersonCommandData',
        identificationCreationData: {
          _type: 'IdentificationCreationData',
          type: 'person',
        } satisfies IdentificationCreationData,
      })}
      fetchFrame={() => Promise.resolve({ _type: 'AnyVFrame' })}
      validationSchema={formSchema}
      {...props}
    >
      <FormikSelect
        name={validateKey('informationSource')}
        label={translate('card.field.informationSource')}
        options={informationSources}
        renderFn={(x) => translateEnum('legalEntity.enum.InformationSource', x)}
      />
      {/* These are only part of PersonCreationDataV2 and are therefor only shown on create */}
      {commandActionVariant === 'create' && (
        <>
          <FormikCheckbox
            name={validateKey('identificationCreationData.pseudoExternalId')}
            label={translate('createLegalEntityModalButton.pseudoExternalId')}
          />
          <FormikTextInput
            name={validateKey('identificationCreationData.externalId')}
            label={translate('createLegalEntityModalButton.externalId')}
            isRequired
          />
        </>
      )}
      <FormikTextInput
        name={validateKey('firstName')}
        label={translate('createPersonCommandData.firstName')}
      />
      <FormikTextInput
        name={validateKey('lastName')}
        label={translate('createPersonCommandData.lastName')}
      />
      <FormikTextInput
        name={validateKey('initials')}
        label={translate('createPersonCommandData.initials')}
      />
      <FormikDatePicker
        name={validateKey('birthDate')}
        label={translate('createPersonCommandData.birthdate')}
        isRequired
      />
      <FormikSelect
        name={validateKey('gender')}
        label={translate('createPersonCommandData.gender')}
        options={genderOptions}
      />
      <FormikTextInput
        name={validateKey('nationality')}
        label={translate('createPersonCommandData.nationality')}
      />
      <FormikFieldArray
        name={validateKey('addressLinks')}
        label={translate('addressLink.field.addressLinks')}
        elementLabel={translate('addressLink.field.addressLink')}
        elementInitializer={newDefaultAddressLink}
        ChildElement={AddressLinkFields}
      />
    </LifeLazyForm>
  )
}
